// Core Types for Access Project Management

// Domain-specific types
export * from './timesheet';
export * from './expense';

// Domain-specific types
export * from './timesheet';
export * from './expense';

// Project status IDs: 1=Planning, 2=In Progress, 3=On Hold, 4=Reopened, 5=Cancelled, 6=Completed, 7=Opportunity
export type ProjectStatusId = 1 | 2 | 3 | 4 | 5 | 6 | 7;

// Only the statuses available in the system's status picker
export const PROJECT_STATUS_LABELS: Record<number, string> = {
  1: 'Planning',
  2: 'In Progress',
  3: 'On Hold',
  5: 'Cancelled',
  6: 'Completed',
};
export type ProjectHealth = 'green' | 'amber' | 'red';
export type ProjectType = 'fixed-price' | 'time-and-materials' | 'retainer';
export type RevenueRecognitionMethod = 'percentage-of-completion' | 'milestone' | 'time-based';
export type ChangeRequestStatus = 'draft' | 'pending' | 'approved' | 'rejected';

// Unified normalized status for all approval workflows (Time, Expense, Purchase Request)
export type NormalizedApprovalStatus = 
  | 'draft' 
  | 'submitted' 
  | 'pending'
  | 'approved' 
  | 'rejected' 
  | 'resubmitted'
  | 'redirected'   // PR-specific
  | 'reopened'     // PR-specific
  | 'posted'       // PR-specific
  | 'cancelled' 
  | 'unknown';

export type FeatureMode = 'live' | 'demo';
export type DataMode = 'demo' | 'live' | 'tour';

/**
 * Import and re-export BillingTriggerType so callers can import it from either
 * budget.types or index without a separate import path.
 */
import type { BillingTriggerType } from './budget.types';
export type { BillingTriggerType };

export interface Address {
  line1: string;
  line2?: string;
  city: string;
  county?: string;
  postcode: string;
  country: string;
}

export interface ClientContact {
  name: string;
  email?: string;
  phone?: string;
  role: 'primary' | 'billing' | 'accounts-payable' | 'legal' | 'technical';
}

export interface Client {
  id: number;
  name: string;
  code?: string;  // Customer code (e.g., "ACME-001")
  // industry removed — no equivalent field in Finance System
  contactName: string;
  contactFirstName?: string;
  contactEmail: string;
  phoneNumber?: string;  // Contact telephone number synced from Finance System
  logo?: string;
  vatNumber?: string;  // VAT registration number
  invoiceAddress?: Address;   // Billing address
  deliveryAddress?: Address;  // Delivery address (if different from invoice)

  // Account Manager (fixes the unimplemented stub)
  accountManagerName?: string;  // Denormalised display name from Finance System

  // Finance System — Identification
  sortKey?: string;           // Sort/grouping key used in Finance reporting (e.g. group consolidation)
  customFields?: Record<string, string>; // User-defined character (UDC) fields pulled from Finance System
  currencyCode?: string;      // Default trading currency (e.g. "GBP", "EUR", "USD")

  // Finance System — Commercial (Payment Terms from Finance System)
  // Due Date — the contractual payment deadline
  dueDateType?: 'days-from-invoice' | 'end-of-following-month' | 'day-of-following-month' | 'days-after-end-of-month';
  dueDateDays?: number;        // Number of days (used when dueDateType is days-based)
  // Anticipated Payment — predicted actual payment based on client history
  anticipatedPaymentDays?: number;   // Number of days
  anticipatedPaymentType?: 'from-invoice-date' | 'from-due-date';


  // Finance System — Financial Standing (read-only from Finance System)
  creditLimit?: number;
  currentBalance?: number;    // Total outstanding balance (invoiced, not yet paid)
  overdueBalance?: number;    // Portion of currentBalance that is past due date
  accountStatus?: 'active' | 'on-stop' | 'suspended' | 'prospect' | 'closed';

  // Multiple Contacts (replaces/extends single contactName/contactEmail)
  contacts?: ClientContact[];
}

export interface Employee {
  id: number;
  name: string;
  email: string;
  role: string;
  department: string;
  costRate: number;
  billRate: number;
  skills: string[];
  photo?: string;
  isAvailable: boolean;
  /** Resource type synced from PSA Resources, e.g. Person, Contractor, Asset. */
  type?: string;
  externalUserId?: string;
  workingPatternName?: string;
  hoursPerWeek?: number;
  workingDays?: string;
  hoursPerDay?: string;
}

export interface ResourceAllocation {
  id: number;
  projectId: number;
  employeeId: number;
  startDate: string;
  endDate: string;
  role: string;
  /** Backend returns roleName from the Role table JOIN; mock data uses role */
  roleName?: string;
  // Core allocation fields - use either allocationPercent or allocatedHours
  allocationPercent?: number;
  allocatedHours?: number;
  utilisationPercent?: number;
  // Optional display-friendly fields for UI
  projectName?: string;
  employeeName?: string;
  // Status fields (optional for mock data scenarios)
  status?: 'confirmed' | 'tentative';
}

// ── Project Role Assignments ──────────────────────────────────────────────
// Structured accountability roles for a project. All roles resolve to
// internal employees from the Employee table.

export type ProjectRoleType =
  | 'project-manager'
  | 'project-lead'
  | 'technical-lead'
  | 'project-sponsor'
  | 'delivery-manager'
  | 'billing-contact'
  | 'engagement-partner'
  | 'qa-lead';

export const PROJECT_ROLE_LABELS: Record<ProjectRoleType, string> = {
  'project-manager':    'Project Manager',
  'project-lead':       'Project Lead',
  'technical-lead':     'Technical Lead',
  'project-sponsor':    'Project Sponsor',
  'delivery-manager':   'Delivery Manager',
  'billing-contact':    'Billing Contact',
  'engagement-partner': 'Engagement Partner',
  'qa-lead':            'QA Lead',
};

// Controls rendering order in the Key People card.
// Ordered by accountability hierarchy: commercial → delivery → technical → finance.
export const PROJECT_ROLE_DISPLAY_ORDER: ProjectRoleType[] = [
  'project-manager',
  'project-sponsor',
  'engagement-partner',
  'delivery-manager',
  'project-lead',
  'technical-lead',
  'billing-contact',
  'qa-lead',
];

/**
 * ProjectNote — a single immutable entry in the Project Notes log.
 *
 * Design decision (v1.4): Notes are a timestamped audit log, NOT a free-text field.
 * Each entry is appended by the creating user and cannot be edited after a grace period.
 * This preserves full history for audit, handover, and accountability purposes.
 *
 * Backend wiring: TODO [ENGINEERING — Devin]
 *   GET  /api/projects/{id}/notes        → returns ProjectNote[] newest-first
 *   POST /api/projects/{id}/notes        → body: { content: string } → returns ProjectNote
 *   PATCH /api/projects/{id}/notes/{noteId} → body: { content: string } (within grace period only)
 *   DELETE /api/projects/{id}/notes/{noteId} → soft-delete (admin/manager only)
 */
export interface ProjectNote {
  id: string;                   // UUID
  projectId: number;
  content: string;              // Plain text — whitespace: pre-wrap on render
  createdAt: string;            // ISO 8601 timestamp
  createdByName: string;        // Display name at time of writing — denormalised for immutability
  createdByInitials: string;    // Avatar fallback — e.g. "JD" from "Jane Doe"
  isEdited?: boolean;           // True if edited within grace period
  editedAt?: string;            // ISO 8601 — when it was last edited
  isDeleted?: boolean;          // Soft-delete flag
}

export interface ProjectRoleAssignment {
  id: number;
  projectId: number;
  employeeId: number;
  employeeName: string;       // Denormalised — avoids join on every card render
  employeeJobTitle?: string;  // Employee job title for display context under name
  role: ProjectRoleType;
}

export interface ProjectRoleConfig {
  role: ProjectRoleType;
  enabled: boolean;
  required: boolean;
  displayLabel?: string; // Overrides PROJECT_ROLE_LABELS[role] when set
}

export interface CompanyProjectSettings {
  roleConfigs: ProjectRoleConfig[];
}

// ── Project Custom Fields (Project Attributes) ────────────────────────────
// Company-configured typed fields that appear on the Project Attributes card
// on the Overview tab. Field definitions are stored in company settings;
// per-project values are stored on the Project record.

export type CustomFieldType = 'text' | 'number' | 'currency' | 'date' | 'boolean' | 'select';

export interface ProjectCustomFieldDefinition {
  id: string;             // UUID — used as key in Project.projectCustomFields
  label: string;          // Display label, e.g. "Contract Reference"
  fieldType: CustomFieldType;
  required: boolean;
  enabled: boolean;
  displayOrder: number;   // Controls rendering sequence in the card
  options?: string[];     // Only used when fieldType === 'select'
  placeholder?: string;   // Optional hint text in edit mode inputs
}

export interface Project {
  id: number;
  name: string;
  code?: string;  // Project code used for external system integrations (e.g., FinOps)
  // Many-to-many relationship with clients (replaces single clientId)
  clientIds?: string;  // Comma-separated list of client IDs
  clientNames?: string;  // Comma-separated list of client names (populated by stored procedure)
  projectManagerId: number;
  type: ProjectType;
  statusId: ProjectStatusId;
  health: ProjectHealth;
  startDate: string;
  endDate: string;
  forecastEndDate?: string;   // PM-entered forecast completion date. Optional — absent until set by PM.
                              // When set and later than endDate, signals a schedule overrun.
  description: string;
  
  // Financial
  budgetLabour: number;
  budgetExpense: number;
  budgetRevenue: number;
  actualLabour: number;
  actualExpense: number;
  actualRevenue: number;

  // Sync-derived actuals from WIPLedger (BE-001)
  costActual?: number;
  salesActual?: number;

  // Authoritative total cost budget — CostBudget.TotalBudget, falling back to
  // budgetLabour + budgetExpense when no cost budget exists.
  budgetCost?: number;

  // Profitability — sourced from the Project Transactions report (financials system).
  // actualSales = SUM(transaction.actualSales) for all transactions on this project.
  //               Represents real invoices raised against the project.
  // actualCosts = SUM(transaction.actualCost) for all transactions on this project.
  //               Captures ALL cost types: labour, expenses, subcontractors, purchases,
  //               materials, equipment, overhead — nothing excluded.
  //
  // Derived:
  //   Gross Profit £  = actualSales - actualCosts
  //   Gross Margin %  = (actualSales - actualCosts) / actualSales × 100
  //
  // These two fields are the ONLY inputs used for all profitability calculations.
  // Do NOT use actualLabour, actualExpense, or marginPercent for profitability.
  actualSales: number;
  actualCosts: number;

  // GP method cost breakdowns — optional fields populated when nominal code mapping (Task 24)
  // is configured. Used by the 'contribution' and 'fully-loaded' GP calculation methods.
  // Without these, the 'contribution' method treats labour+subs as zero (falls back to simple).
  // 🔒 Blocked on backend nominal code mapping (FINANCIALS_TAB_BACKEND_CHANGES.md).
  actualLabourCosts?: number;
  actualSubcontractorCosts?: number;
  actualOverheadAbsorption?: number;

  // Change Control — budget baseline fields populated by backend when budget is first saved.
  originalBudgetRevenue?: number;
  originalBudgetCosts?: number;
  budgetBaselinedAt?: string;
  budgetRevisionCount?: number;
  lastBudgetRevisedAt?: string;
  /** @deprecated Use originalBudgetRevenue instead */
  originalBudget?: number;

  // Budget variances — derived from their respective budget tab totals
  // salesVariance         = budgetRevenue - actualRevenue
  // salesVariancePercent  = salesVariance / budgetRevenue × 100
  // costVariance          = (budgetLabour + budgetExpense) - (actualLabour + actualExpense)
  // costVariancePercent   = costVariance / (budgetLabour + budgetExpense) × 100
  salesVariance: number;
  salesVariancePercent: number;
  costVariance: number;
  costVariancePercent: number;
  
  // Hours/Resource Budget
  budgetHours: number;
  actualHours: number;
  allocatedHours?: number | null;
  billableHours?: number | null;
  capacityHours?: number | null;
  // Realisation rate % (actual billed revenue / potential billable value × 100),
  // computed server-side from WIPLedger. Feeds the health rollup and its tooltip.
  realisationRatePct?: number | null;
  hoursVariance: number;
  hoursVariancePercent: number;
  
  // @deprecated — use actualSales and actualCosts instead.
  // marginPercent was a manually maintained denormalised value with no reliable formula.
  // Gross Margin % is now always derived: (actualSales - actualCosts) / actualSales × 100
  marginPercent: number;
  
  // Progress
  completionPercent: number;

  // Completion % calculation mode — fixed-price % complete projects only.
  // - 'based-on-time' — auto-calculated from approvedHours ÷ budgetHours (default for all projects)
  // - 'manual'        — frozen; PM must update manually via the Project Overview
  completionPercentMode?: 'manual' | 'based-on-time';

  // PM override — set when the PM manually adjusts the completion % via Project Overview.
  // When present, this value is used instead of the auto-calculated figure.
  completionPercentOverride?: number;

  // Audit fields populated when a PM override is set or cleared.
  completionPercentOverriddenBy?: string;   // Name of the user who set the override
  completionPercentOverriddenAt?: string;   // ISO timestamp of when the override was set
  
  // Billable flag — whether this project is billable to the client.
  // Defaults to true. Set to false for internal, pro-bono, or non-chargeable projects.
  // When set to false the user is prompted to optionally cascade to all cost centres.
  billable?: boolean;
  cascadeBillableToCostCentres?: boolean;

  // Billing trigger type — fixed-price projects only.
  // Determines how billing milestones are evaluated for all milestones on this project.
  // - 'date'             — milestone fires on dueDate (default for T&M / retainer / date-based Fixed Fee)
  // - 'percent-complete' — milestone fires when completionPercent >= milestone.triggerPercent
  // Optional so the field can be absent on T&M / retainer projects and older mock data.
  billingTriggerType?: BillingTriggerType;
  
  // Account Manager — internal employee responsible for the customer relationship on this project.
  // Separate from client.accountManagerName (Finance System string).
  // FK to Employee. Optional — not all projects will have one assigned.
  accountManagerId?: number;

  // Revenue Recognition
  revenueRecognitionMethod: RevenueRecognitionMethod;
  
  // WIP
  wipAmount: number;
  unbilledAmount: number;
  
  // Metadata
  createdAt: string;
  updatedAt: string;

  // Key People — role-based accountability assignments.
  // Replaces flat projectManagerId in new projects.
  // projectManagerId is retained as a read-only fallback during migration.
  roleAssignments?: ProjectRoleAssignment[];

  // Comma-separated list of employee IDs assigned to any project role (Key People),
  // populated by the projects list stored procedure. Used by the "Managed & Allocated"
  // visibility filter so key people (not just the PM) can see their projects.
  keyPeopleEmployeeIds?: string;

  // Project Content Fields — long-form narrative fields (Workstream E).
  // Stored as plain text. No rich-text/HTML — newlines preserved with whitespace: pre-wrap.
  // All optional: existing projects will not have these populated on day one.
  objectives?: string;       // Why the project exists — goals and success criteria
  keyDeliverables?: string;  // What the project produces — tangible outputs and artefacts

  // Notes is a timestamped audit log — NOT a free-text field (v1.4 decision).
  // Each entry is immutable after a grace period. Full history is preserved.
  // Migration: existing projects with a legacy `notes` string will have it surfaced
  // as a single seed entry by the backend migration (see F3 migration notes).
  noteLog?: ProjectNote[];   // Ordered newest-first. Populated from GET /api/projects/{id}/notes

  // Project Attributes — typed custom fields configured per company (Section 7).
  // Key = ProjectCustomFieldDefinition.id (UUID)
  // Value = stored as string; cast to typed value on read using fieldType.
  // Only fields with non-empty values are stored here.
  projectCustomFields?: Record<string, string>;

}

export interface ProjectPhase {
  id: number;
  projectId: number;
  name: string;
  startDate: string;
  endDate: string;
  budget: number;
  actual: number;
  completionPercent: number;
}

export interface Task {
  id: number;
  projectId: number;
  phaseId?: number;
  name: string;
  description: string;
  assigneeId?: number;
  status: 'todo' | 'in-progress' | 'completed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  dueDate: string;
  estimatedHours: number;
  actualHours: number;
}

export interface ApprovalHistoryEntry {
  date: string; // ISO timestamp
  action: string; // e.g. Approved, Rejected, Submitted
  byUser: string; // user performing the action
  stage?: string; // optional workflow stage
  toUser?: string | string[]; // approver(s) - single or multiple users
  status: string; // resulting status
}

// Attachment reference for timesheet evidence (receipts, time proof, etc.)
export interface AttachmentRef {
  id: string;
  fileName: string;
  url?: string; // or file store key
  uploadedAt: string;
  uploadedBy: string;
}

// Status mapping for tracking how source system statuses map to normalized statuses
export interface StatusMapping {
  sourceStatus: string;
  mappedTo: NormalizedApprovalStatus;
  mappedAt: string;
}

export interface Milestone {
  id: number;
  projectId: number;
  name: string;
  dueDate: string;
  completedDate?: string;
  status: 'pending' | 'completed' | 'overdue';
  paymentAmount?: number;
  paymentReceived: boolean;
}

export interface ChangeRequest {
  id: number;
  projectId: number;
  title: string;
  description: string;
  requestedBy: string;
  requestedDate: string;
  status: ChangeRequestStatus;
  impactBudget: number;
  impactSchedule: number; // days
  approvedBy?: string;
  approvedDate?: string;
}

export type WipAgeBand = '0-30' | '31-60' | '61-90' | '91-120' | '121+';

export type WipAgingBands = Record<WipAgeBand, number>;

export interface WIPReport {
  projectId: number;
  projectName: string;
  clientName: string;
  pmName?: string;
  /** Status of the underlying project — see PROJECT_STATUS_LABELS */
  statusId?: number;
  projectType?: 'time-and-materials' | 'fixed-price' | 'retainer';
  /** Whether this project is billable to the client. Defaults to true. */
  billable?: boolean;
  costsIncurred: number;
  profitEarned: number;
  amountBilled: number;
  wipAmount: number;
  /** Age in days of the project's oldest unbilled WIP ledger entry */
  agingDays: number;
  /**
   * wipAmount split across the aging bands, banded by the date of each unbilled WIP ledger
   * entry so the split matches the project Billing tab. Absent in demo/tour data, where the
   * whole amount is treated as sitting in the band implied by agingDays.
   */
  agingBands?: WipAgingBands;
  readyToBill: boolean;
  overBilling: number;
  underBilling: number;
  /** ISO date string — present for date-based milestones (and % complete milestones that have a deadline) */
  nextBillingEvent?: string;
  nextBillingEventAmount?: number;
  /** Trigger type of the next billing event — present for fixed-price projects */
  nextBillingEventTriggerType?: 'date' | 'percent-complete';
  /**
   * The % threshold for the next billing event.
   * Only present when nextBillingEventTriggerType === 'percent-complete'.
   */
  nextBillingEventTriggerPercent?: number;
  /** Total value of WIP written off for this project in the current financial period */
  writtenOffAmount?: number;
}

export interface UtilisationData {
  employeeId: number;
  employeeName: string;
  role: string;
  department: string;
  billableHours: number;
  availableHours: number;
  utilisationPercent: number;
  targetPercent: number;
  status: 'under' | 'target' | 'over';
}

export interface PortfolioSummary {
  totalProjects: number;
  activeProjects: number;
  portfolioValue: number;
  totalWIP: number;
  avgMarginPercent: number;
  avgUtilisationPercent: number;
  projectsByStatus: Record<string, number>;
  projectsByHealth: Record<ProjectHealth, number>;
}

export interface IntegrationStatus {
  service: 'access-dimensions' | 'access-financials' | 'timesheet-system' | 'resource-scheduling';
  status: 'connected' | 'syncing' | 'error' | 'disconnected';
  lastSync?: string;
  errorMessage?: string;
}

export interface FeatureLabsItem {
  id: number;
  name: string;
  description: string;
  category: 'preview' | 'coming-soon' | 'live';
  status: FeatureMode;
  availableDate?: string;
  route?: string;
}

export interface UserPreferences {
  userId: string;
  featureModes: Record<string, FeatureMode>;
  sensitiveDataVisible: boolean;
  theme: 'light' | 'dark' | 'system';
}

// ============================================================================
// COMPLETION PERCENT AUDIT TRAIL
// ============================================================================

/**
 * A single entry in the completion % audit trail for a project.
 * Sourced from `mockCompletionAuditData` in mock-data.ts (keyed by project ID).
 */
export interface CompletionPercentAuditEntry {
  id: string;
  projectId: number;
  changedBy: string;         // User ID or identifier
  changedByName: string;     // Display name
  /** How the value changed */
  changeType: 'auto-calculation' | 'manual-override' | 'override-cleared';
  previousValue: number;     // % before the change
  newValue: number;          // % after the change
  changedAt: string;         // ISO timestamp
  /** Approved hours at the time of the change — present for auto-calculation entries */
  approvedHoursAtChange?: number;
  /** Budget hours at the time of the change — present for auto-calculation entries */
  budgetHoursAtChange?: number;
}

// Export budget types
export * from './budget.types';

// Export report types (ProjectPLReport, BudgetVarianceReport re-exports, etc.)
export * from './reports.types';

// Export category types
export * from './category.types';

// Export company types
export * from './company';

// Export assignment types
export * from './assignment.types';
